"use client";

// Built on Base UI's unstyled Tooltip — see full API at:
// https://base-ui.com/react/components/tooltip

import React from "react";

import { Tooltip as BaseTooltip } from "@base-ui/react/tooltip";

import { cn } from "@marketing-sites/utils/cn";

export interface RootProps extends BaseTooltip.Root.Props {}

export function Root(props: RootProps) {
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (!open) return;
    const dismiss = () => setOpen(false);
    window.addEventListener("scroll", dismiss, {
      passive: true,
      capture: true,
    });
    return () =>
      window.removeEventListener("scroll", dismiss, { capture: true });
  }, [open]);

  return <BaseTooltip.Root {...props} open={open} onOpenChange={setOpen} />;
}

// Hover open/close timing belongs here (shared group) or on Tooltip.Trigger (per-tooltip), not on Root.
export const Provider = BaseTooltip.Provider;

export const Trigger = BaseTooltip.Trigger;

// Intercept the Portal and bind it to our nested Layouts rather than body tag
// This allows for fonts to correctly work
export function Portal({
  container,
  ...rest
}: React.ComponentProps<typeof BaseTooltip.Portal>) {
  const rootRef = React.useRef<HTMLElement | null>(null);

  if (typeof document !== "undefined" && !rootRef.current && !container) {
    rootRef.current = document.querySelector(".root") ?? document.body ?? null;
  }

  return <BaseTooltip.Portal container={container ?? rootRef} {...rest} />;
}

export function Positioner({
  className,
  sideOffset = 8,
  ...rest
}: React.ComponentProps<typeof BaseTooltip.Positioner>) {
  return (
    <BaseTooltip.Positioner
      className={className}
      sideOffset={sideOffset}
      {...rest}
    />
  );
}

export function Popup({
  className,
  ...rest
}: React.ComponentProps<typeof BaseTooltip.Popup>) {
  return (
    <BaseTooltip.Popup
      className={cn("z-50 max-w-xs bg-black px-3 py-2 text-white", className)}
      {...rest}
    />
  );
}

export function Arrow({
  className,
  ...rest
}: React.ComponentProps<typeof BaseTooltip.Arrow>) {
  return (
    <BaseTooltip.Arrow
      className={cn(
        "size-2.5 rotate-45 bg-black data-[side=bottom]:-top-1 data-[side=left]:-right-1 data-[side=right]:-left-1 data-[side=top]:-bottom-1",
        className,
      )}
      {...rest}
    />
  );
}
